// Thin wrapper around Telegram WebApp globals.
// Works whether we're inside Telegram or in a browser (dev mode).

export function getTelegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function isInsideTelegram(): boolean {
  const tg = getTelegramWebApp();
  return Boolean(tg && tg.initData && tg.initData.length > 0);
}

export function initTelegramWebApp(): void {
  const tg = getTelegramWebApp();
  if (!tg) return;
  tg.ready();
  tg.expand();
  // Kill the iOS "swipe down to dismiss" gesture. Users kept losing their
  // place by accidentally pulling down while scrolling lists. Available
  // from Bot API 7.7 (June 2024); older clients silently no-op.
  if (
    typeof tg.isVersionAtLeast === "function" &&
    tg.isVersionAtLeast("7.7") &&
    typeof tg.disableVerticalSwipes === "function"
  ) {
    tg.disableVerticalSwipes();
  }
}

/**
 * Apply Telegram themeParams to our CSS variables.
 * Call once on mount and re-run on "themeChanged".
 */
export function applyTelegramTheme(): () => void {
  const tg = getTelegramWebApp();
  if (!tg) return () => {};

  const apply = () => {
    const root = document.documentElement;
    const { themeParams, colorScheme } = tg;

    root.setAttribute("data-theme", colorScheme);

    const map: Record<string, string | undefined> = {
      "--tg-theme-bg-color": themeParams.bg_color,
      "--tg-theme-text-color": themeParams.text_color,
      "--tg-theme-hint-color": themeParams.hint_color,
      "--tg-theme-link-color": themeParams.link_color,
      "--tg-theme-button-color": themeParams.button_color,
      "--tg-theme-button-text-color": themeParams.button_text_color,
      "--tg-theme-secondary-bg-color": themeParams.secondary_bg_color,
      "--tg-theme-section-bg-color": themeParams.section_bg_color,
      "--tg-theme-section-separator-color": themeParams.section_separator_color,
      "--tg-theme-section-header-text-color": themeParams.section_header_text_color,
      "--tg-theme-subtitle-text-color": themeParams.subtitle_text_color,
      "--tg-theme-destructive-text-color": themeParams.destructive_text_color,
      "--tg-theme-header-bg-color": themeParams.header_bg_color,
      "--tg-theme-bottom-bar-bg-color": themeParams.bottom_bar_bg_color,
    };

    for (const [name, value] of Object.entries(map)) {
      if (value) root.style.setProperty(name, value);
    }
  };

  apply();
  tg.onEvent("themeChanged", apply);
  return () => tg.offEvent("themeChanged", apply);
}

export function haptic(style: "light" | "medium" | "heavy" = "light"): void {
  const tg = getTelegramWebApp();
  // HapticFeedback requires Telegram WebApp 6.1+. Calling it on 6.0 triggers
  // a noisy console.warn from telegram-web-app.js.
  if (!tg || typeof tg.isVersionAtLeast !== "function" || !tg.isVersionAtLeast("6.1")) {
    return;
  }
  tg.HapticFeedback?.impactOccurred(style);
}

/**
 * Returns the TG LocationManager if the host client supports it (Bot API 8.0+
 * → TG WebApp 7.10+). Older clients and plain browsers return null — callers
 * should fall back to `navigator.geolocation`.
 */
export function getLocationManager(): TelegramLocationManager | null {
  const tg = getTelegramWebApp();
  if (!tg || typeof tg.isVersionAtLeast !== "function") return null;
  if (!tg.isVersionAtLeast("7.10")) return null;
  return tg.LocationManager ?? null;
}

/**
 * Initialise the LocationManager once and resolve when ready. Resolves to
 * the manager itself on success, or `null` if init never fires (e.g. very
 * old client). Safe to call multiple times — TG no-ops further inits.
 */
export function ensureLocationManagerInited(
  timeoutMs = 3000,
): Promise<TelegramLocationManager | null> {
  const lm = getLocationManager();
  if (!lm) return Promise.resolve(null);
  if (lm.isInited) return Promise.resolve(lm);
  return new Promise((resolve) => {
    let settled = false;
    const done = (v: TelegramLocationManager | null) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    lm.init(() => {
      clearTimeout(timer);
      done(lm);
    });
  });
}
